import { Paper, Typography, TextField, Grid, Button, Select, Checkbox, MenuItem } from "@mui/material";
import { SvgIcon } from '@mui/material';
import { blankSignatureRows } from '../Util/BlankItems';

const LeftRightIcon = ({ checked }) => (
    <SvgIcon
        viewBox="0 0 100 20"
        sx={{ width: "100px", height: "20px" }} >
        <rect width="100" height="20" fill={checked ? "green" : "gray"} />
    </SvgIcon>
);

const UpDownIcon = ({ checked }) => (
    <SvgIcon
        viewBox="0 0 20 50"
        sx={{ width: "20px", height: "50px" }}
    >
        <rect width="20" height="50" fill={checked ? "green" : "gray"} />
    </SvgIcon>
);


const SignatureAbilityEdit = ({ editedItem, index, appFiles, setAppFiles }) => {

    const careers = JSON.parse(appFiles.careers);
    const allNodes = JSON.parse(appFiles.signatureAbilityNodes);

    // The sig-ability nodes link back through a "SigAbility" field that matches
    // this ability's Name, but that data is inconsistent (typos, plurals). Offer
    // the declared nodes plus any key already used in the tree so nothing renders
    // blank, and fall back to every node when the match comes up empty.
    const usedKeys = new Set(
        (editedItem.AbilityRows || []).flatMap((row) => row.Abilities)
    );
    let nodes = allNodes.filter(
        (n) => n.SigAbility === editedItem.Name || usedKeys.has(n.Key)
    );
    if (nodes.length === 0) { nodes = allNodes; }

    const handleEdit = (newItem) => {
        let newFiles = { ...appFiles };
        let newCat = JSON.parse(appFiles["signatureAbilities"]);
        newCat[index] = newItem;
        newFiles["signatureAbilities"] = JSON.stringify(newCat);
        setAppFiles(newFiles);
        localStorage.setItem('appFiles', JSON.stringify(newFiles));
    };

    // Clone the rows and coerce any "" / non-object Direction cell into an object
    // before mutating, so toggling a connection never throws on the messier
    // signature-ability data.
    const cloneRows = () => JSON.parse(JSON.stringify(editedItem.AbilityRows));
    const ensureDir = (rows, r, i) => {
        if (!rows[r].Directions[i] || typeof rows[r].Directions[i] !== 'object') {
            rows[r].Directions[i] = {};
        }
    };

    return (
        <Paper>
            <Typography>Career:</Typography>
            <Select
                value={editedItem.Career || ''}
                onChange={(e) => handleEdit({ ...editedItem, Career: e.target.value })}
                sx={{ minWidth: "200px" }}
            >
                {careers
                    .sort((a, b) => a.Name.localeCompare(b.Name))
                    .map((career) => (
                        <MenuItem key={career.Key} value={career.Key}>
                            {career.Name}
                        </MenuItem>
                    ))}
            </Select>
            <Typography sx={{ marginTop: "10px" }}>Tree Connection Points (MatchingNodes):</Typography>
            <Grid container>
                {Array.from({ length: 4 }).map((_, i) => (
                    <Grid item key={i} sx={{ textAlign: "center", width: "60px" }}>
                        <Typography variant="caption">{i + 1}</Typography>
                        <Checkbox
                            checked={!!editedItem.MatchingNodes?.[i]}
                            onChange={() => {
                                let newNodes = [
                                    !!editedItem.MatchingNodes?.[0],
                                    !!editedItem.MatchingNodes?.[1],
                                    !!editedItem.MatchingNodes?.[2],
                                    !!editedItem.MatchingNodes?.[3],
                                ];
                                newNodes[i] = !newNodes[i];
                                handleEdit({ ...editedItem, MatchingNodes: newNodes });
                            }}
                        />
                    </Grid>
                ))}
            </Grid>
            <Typography sx={{ marginTop: "10px" }}>Ability Grid:</Typography>
            {!editedItem.AbilityRows?.length && (
                <Button className="no-print" onClick={() => handleEdit({ ...editedItem, AbilityRows: blankSignatureRows() })}>
                    Create Ability Grid
                </Button>
            )}
            {(editedItem.AbilityRows || []).map((row, index) => (
                <>
                    <Grid container key={index} spacing={2}>
                        {row.Abilities.map((ability, i) => (
                            <Grid item xs={parseInt(row.AbilitySpan[i]) * 3} key={i}>
                                <Grid container>
                                    <Grid
                                        item
                                        xs={4 / parseInt(row.AbilitySpan[i])}
                                        sx={{
                                            position: "relative",
                                            paddingTop: "10px",
                                        }}
                                    >
                                        {i > 0 && (
                                            <Grid container direction="column" alignItems="center" fullWidth>
                                                <Grid item>
                                                    <Button
                                                        onClick={() => {
                                                            let newRows = cloneRows();
                                                            let newRow = newRows[index];
                                                            if (i > 0 && i < newRow.Abilities.length) {
                                                                newRow.AbilitySpan[i - 1] = parseInt(newRow.AbilitySpan[i - 1]) + parseInt(newRow.AbilitySpan[i]);
                                                                newRow.Abilities.splice(i, 1);
                                                                newRow.AbilitySpan.splice(i, 1);
                                                                handleEdit({ ...editedItem, AbilityRows: newRows });
                                                            }
                                                        }}
                                                    >
                                                        {"⟹ ⟸"}
                                                    </Button>

                                                </Grid>
                                                <Grid item>
                                                    <Checkbox
                                                        checked={!!(row.Directions[i]?.Left && row.Directions[i - 1]?.Right)}
                                                        onChange={() => {
                                                            let newRows = cloneRows();
                                                            ensureDir(newRows, index, i);
                                                            ensureDir(newRows, index, i - 1);
                                                            newRows[index].Directions[i].Left = !newRows[index].Directions[i].Left;
                                                            newRows[index].Directions[i - 1].Right = !newRows[index].Directions[i - 1].Right;
                                                            handleEdit({ ...editedItem, AbilityRows: newRows });
                                                        }}
                                                        icon={<LeftRightIcon />}
                                                        checkedIcon={<LeftRightIcon checked />}
                                                    />
                                                </Grid>
                                            </Grid>
                                        )}
                                    </Grid>
                                    <Grid item xs={8 + (4 - (4 / parseInt(row.AbilitySpan[i])))}>
                                        <Grid container direction="column" alignItems="center" fullWidth>
                                            <Grid item>
                                                <Button
                                                    disabled={!(row.AbilitySpan[i] > 1)}
                                                    onClick={() => {
                                                        let newRows = cloneRows();
                                                        let newRow = newRows[index];
                                                        const currentSpan = parseInt(newRow.AbilitySpan[i]);
                                                        if (currentSpan > 1) {
                                                            newRow.AbilitySpan[i] = 1;
                                                            newRow.Abilities.splice(i + 1, 0, "");
                                                            newRow.AbilitySpan.splice(i + 1, 0, currentSpan - 1);
                                                            handleEdit({ ...editedItem, AbilityRows: newRows });
                                                        }
                                                    }}
                                                >
                                                    {"⟺"}
                                                </Button>

                                            </Grid>
                                            <Grid item sx={{ width: "100%" }}>
                                                <Grid container alignItems="center">
                                                    <Grid item sx={{ flexGrow: 1 }}>
                                                        <Select
                                                            value={ability}
                                                            onChange={(e) => {
                                                                let newRows = cloneRows();
                                                                newRows[index].Abilities[i] = e.target.value;
                                                                handleEdit({ ...editedItem, AbilityRows: newRows });
                                                            }}
                                                            sx={{ background: "black", zIndex: 50, width: "100%" }}
                                                        >
                                                            {nodes
                                                                .sort((a, b) => a.Name.localeCompare(b.Name))
                                                                .map((node) => (
                                                                    <MenuItem key={node.Key} value={node.Key}>
                                                                        {node.Name}
                                                                    </MenuItem>
                                                                ))}
                                                        </Select>
                                                    </Grid>
                                                    <Grid item sx={{ width: 'auto', paddingLeft: 1 }}>
                                                        <TextField
                                                            value={row.Costs[i]}
                                                            label="Cost"
                                                            sx={{ maxWidth: "60px" }}
                                                            onChange={(e) => {
                                                                let newRows = cloneRows();
                                                                newRows[index].Costs[i] = e.target.value;
                                                                handleEdit({ ...editedItem, AbilityRows: newRows });
                                                            }}
                                                        />
                                                    </Grid>
                                                </Grid>

                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        ))}
                    </Grid>
                    {index < (editedItem.AbilityRows.length - 1) && <Grid container>
                        <Grid item xs={1}></Grid>
                        <Grid item xs={11}>
                            <Grid container justifyContent="center" alignItems="center">
                                {Array.from({ length: 4 }).map((_, i) => (
                                    <Grid item key={i} xs={3}>
                                        <Grid container justifyContent="center" alignItems="center">
                                            <Checkbox
                                                checked={!!(row.Directions[i]?.Down && editedItem.AbilityRows[index + 1].Directions[i]?.Up)}
                                                onChange={() => {
                                                    let newRows = cloneRows();
                                                    ensureDir(newRows, index, i);
                                                    ensureDir(newRows, index + 1, i);
                                                    newRows[index].Directions[i].Down = !newRows[index].Directions[i].Down;
                                                    newRows[index + 1].Directions[i].Up = !newRows[index + 1].Directions[i].Up;
                                                    handleEdit({ ...editedItem, AbilityRows: newRows });
                                                }}
                                                icon={<UpDownIcon />}
                                                checkedIcon={<UpDownIcon checked />}
                                            />
                                        </Grid>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </Grid>}
                </>
            ))}
        </Paper>
    )
}

export default SignatureAbilityEdit
